import type { StoryId } from './story';

export type StoryChapter = {
  id: StoryId;
  number: string;
  title: string;
  body: string;
  facts: readonly string[];
};

export const chapters: readonly StoryChapter[] = [
  {
    id: 'plan',
    number: '00',
    title: 'It starts before the doors — with the presale you almost missed.',
    body: 'Follow the artists you care about and catch presales before the good seats vanish. Keep your tickets together and plan the night around the show.',
    facts: ['Presale + on-sale alerts', 'Artist · venue · event discovery'],
  },
  {
    id: 'log',
    number: '01',
    title: 'See the view. Rate the reality.',
    body: 'Log the night while it is still ringing: capture where you sat and what the view was really worth, then rank it against another show. Your own history decides the score — no five-star guesswork.',
    facts: ['Seat-view reviews: was it worth it?', 'Pairwise ranking, not star ratings'],
  },
  {
    id: 'remember',
    number: '02',
    title: 'Your concert life, rebuilt from the photos you already have.',
    body: 'Sticket finds likely shows from your camera roll — you confirm each one, and your history takes shape. Revisit it by date, city, venue, artist, or the shape of a whole year.',
    facts: ['Import from your camera roll', 'Timeline + collection map'],
  },
  {
    id: 'connect',
    number: '03',
    title: 'Find the people who heard the same encore.',
    body: 'See friends who were there, shared artist history, crowd memories, taste overlap, and plans around the next show.',
    facts: ['Friends who saw the artist', '“I was there too” + shared memories'],
  },
  {
    id: 'feed',
    number: '04',
    title: 'A feed made only of nights worth keeping.',
    body: 'Concert memories from friends, friends of friends, and the wider crowd. No filler between the shows.',
    facts: ['Memory posts only', 'Friends · Friends+ · Public'],
  },
];

export const productFacts = [
  ['3', 'memory audiences', 'Public, friends, or private — chosen per post.'],
  ['4', 'scorecard dimensions', 'Performance, sound, venue, and value. Each is optional.'],
  ['1', 'concert life', 'Timeline, map, artists, venues, tickets, and friends in one history.'],
  ['1', 'local invite-code field', 'It checks only for empty input. Your entry stays available for correction; validation is not active, and the code is not sent or persistently saved.'],
] as const;

export const photoMatchTrust = {
  title: 'Keep photo files off the API.',
  body: 'Photo Match reads metadata on your device. Only timestamps and optional location metadata cross the API; photo files and pixels are not uploaded.',
} as const;

export const faqs = [
  {
    question: 'How do I log a show?',
    answer:
      'Find the show and capture the night — your seat, a photo, a few words — then rank it against another you have been to. Past shows can come straight from your camera roll: Sticket surfaces the likely nights and you confirm them.',
  },
  {
    question: 'How does Sticket score a concert?',
    answer:
      'You compare two nights and pick which one wins. Those choices build a personal ranking of every show you have seen — no five-star guesswork — with optional notes on performance, sound, venue, and value.',
  },
  {
    question: 'What can I see about my concert life?',
    answer:
      'Your whole timeline, a map of every venue and city you have been to, your most-seen artists, milestones, and a year-in-review of your live music.',
  },
  {
    question: 'What are seat-view reviews?',
    answer:
      'Add the view from your seat — a photo, the section, how it actually felt — so you and everyone else can tell whether a spot is worth it before buying.',
  },
  {
    question: 'Are there avatars, badges, and trophies?',
    answer:
      'Yes. You get your own Sticket avatar, and you earn badges and trophies as you rack up shows, venues, and artists over time.',
  },
  {
    question: 'How do I connect Spotify?',
    answer:
      'Connect Spotify to bring in the artists you already listen to — Sticket follows them for you and flags on-sales and presales. It is not a login, and you can disconnect anytime.',
  },
  {
    question: 'When can I get Sticket?',
    answer:
      'Sticket is in private testing on iPhone. There is no public download yet, so this page will not pretend there is — request early access above.',
  },
] as const;
